// Generates a jsPDF document of BLK MRKT thermal labels.
// Native PDF primitives — crisp on thermal printers, B&W safe (no color), small file size.
//
// LAYOUT:
//   • Page is exactly 4x6.
//   • Small orders (≤3 items, items fit) print as 4x3 labels — TWO per 4x6 sheet.
//   • Big orders (overflow 4x3) print as a SINGLE 4x6 label using the full sheet so
//     items stay at full 12pt with single-column flow. Same text proportions; just more
//     vertical space.
//
// Asymmetric white frame (FRAME_T/R/B/L) compensates for the user's Core Solutions thermal
// printer hardware drift that adds extra white on TOP+RIGHT.
import { jsPDF } from 'jspdf';

const W = 4;
const H = 6;
// Uniform white frame on all 4 sides. We previously padded bottom/left more
// to compensate for printer drift, but on full 4x6 labels that asymmetric pad
// looks like off-center content. Equal frame keeps things visually balanced
// regardless of label size and printer behavior.
const FRAME_T = 0.20;
const FRAME_R = 0.20;
const FRAME_B = 0.20;
const FRAME_L = 0.20;
const PAD = 0.16;

const getShippingLabel = (order) => {
  const m = (order.shipping_method || '').toLowerCase().trim();
  if (m) {
    // Admin explicitly set the shipping method via the order card dropdown — honor it.
    if (m.includes('express') || m.includes('overnight')) return 'OVERNIGHT';
    if (m.includes('priority')) return 'PRIORITY';
    return 'GROUND'; // ground_advantage, standard, first_class, etc.
  }
  // No admin override yet — fall back to the boolean shipping flags from checkout.
  if (order.overnight_shipping) return 'OVERNIGHT';
  return order.priority_shipping ? 'PRIORITY' : 'GROUND';
};

const getCustomerName = (order) => {
  const s = order.shipping_address || {};
  return (s.full_name || order.user_name || order.user_email || 'CUSTOMER').toUpperCase();
};

const itemToLines = (doc, it, maxWidth) => {
  const qty = it.quantity || 1;
  const variant = it.variant_name ? ` — ${it.variant_name.toUpperCase()}` : '';
  const name = (it.name || it.product_name || '').toUpperCase();
  let suffix = '';
  if (it.is_trade) suffix = ' [TRADE]';
  else if (it.offer_price !== undefined && it.offer_price !== null) suffix = ' [OFFER]';
  return doc.splitTextToSize(`${qty}x ${name}${variant}${suffix}`, maxWidth);
};

const buildItemLines = (doc, items, maxWidth) => {
  const lines = [];
  (items || []).forEach((it) => {
    itemToLines(doc, it, maxWidth).forEach((w) => lines.push(w));
  });
  return lines;
};

// Draw a label at position (0, yOffset) with given inner content height (regionH).
// regionH = 3 for small (4x3 half-sheet) labels, regionH = 6 for big (full 4x6) labels.
const drawLabel = (doc, order, yOffset, regionH) => {
  // Black area inside the asymmetric white frame.
  const blackLeft = FRAME_L;
  const blackRight = W - FRAME_R;
  const blackTop = yOffset + FRAME_T;
  const blackBottom = yOffset + regionH - FRAME_B;
  doc.setFillColor(0, 0, 0);
  doc.rect(blackLeft, blackTop, blackRight - blackLeft, blackBottom - blackTop, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setDrawColor(255, 255, 255);

  const innerLeft = blackLeft + PAD;
  const innerRight = blackRight - PAD;
  const innerTop = blackTop + PAD;
  const innerBottom = blackBottom - PAD;
  const innerWidth = innerRight - innerLeft;
  const items = order.items || [];

  // ===== TOP BAR =====
  doc.setFont('courier', 'bold');
  doc.setFontSize(15);
  doc.text('BLK MRKT', innerLeft, innerTop + 0.14);
  const shipping = getShippingLabel(order);
  doc.setFontSize(11);
  const shipW = doc.getTextWidth(shipping) + 0.14;
  const shipH = 0.22;
  const shipX = innerRight - shipW;
  const shipY = innerTop;
  doc.setFillColor(255, 255, 255);
  doc.rect(shipX, shipY, shipW, shipH, 'F');
  doc.setTextColor(0, 0, 0);
  doc.text(shipping, shipX + 0.07, shipY + 0.16);
  const topBarBottom = innerTop + 0.30;
  doc.setLineWidth(0.03);
  doc.line(innerLeft, topBarBottom, innerRight, topBarBottom);

  // ===== CUSTOMER NAME =====
  doc.setTextColor(255, 255, 255);
  doc.setFont('courier', 'bold');
  doc.setFontSize(17);
  const custName = getCustomerName(order);
  const custLines = doc.splitTextToSize(custName, innerWidth);
  let custY = topBarBottom + 0.24;
  custLines.slice(0, 2).forEach((line) => {
    doc.text(line, innerLeft, custY);
    custY += 0.22;
  });

  // ===== ORDER ID at the bottom =====
  const orderIdSize = 24;
  const orderIdHeight = 0.34;
  const orderIdY = innerBottom - 0.05;
  const orderIdBorderY = orderIdY - orderIdHeight - 0.06;
  doc.setLineWidth(0.03);
  doc.setDrawColor(255, 255, 255);
  doc.line(innerLeft, orderIdBorderY, innerRight, orderIdBorderY);
  doc.setFont('courier', 'bold');
  doc.setFontSize(orderIdSize);
  doc.setTextColor(255, 255, 255);
  const orderIdText = order.id || '';
  const orderIdW = doc.getTextWidth(orderIdText);
  doc.text(orderIdText, blackLeft + (blackRight - blackLeft - orderIdW) / 2, orderIdY);

  // ===== WEIGHT — hand-write blank line above the order ID separator =====
  // Layout: "WT: ______ OZ" — short underline (1in) is plenty for "12.5" etc.
  const weightLineY = orderIdBorderY - 0.10;
  doc.setFont('courier', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  const weightLabel = 'WT:';
  const weightUnit = 'OZ';
  doc.text(weightLabel, innerLeft, weightLineY);
  const labelW = doc.getTextWidth(weightLabel);
  const unitW = doc.getTextWidth(weightUnit);
  const lineStartX = innerLeft + labelW + 0.06;
  const lineEndX = lineStartX + 1.0; // 1-inch writing space is plenty for ounces
  doc.text(weightUnit, lineEndX + 0.06, weightLineY);
  // Underline for handwriting the number.
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.015);
  doc.line(lineStartX, weightLineY + 0.02, lineEndX, weightLineY + 0.02);

  // ===== ITEMS — single column at full innerWidth, 12pt bold =====
  const ITEM_SIZE = 12;
  const ITEM_LH = 0.21;
  const itemAreaTop = custY - 0.04;
  const itemAreaBottom = weightLineY - 0.10;
  doc.setFont('courier', 'bold');
  doc.setFontSize(ITEM_SIZE);
  doc.setTextColor(255, 255, 255);
  const lines = buildItemLines(doc, items, innerWidth);
  const maxLines = Math.max(1, Math.floor((itemAreaBottom - itemAreaTop) / ITEM_LH));
  let y = itemAreaTop + ITEM_LH;
  lines.slice(0, maxLines).forEach((line) => {
    doc.text(line, innerLeft, y);
    y += ITEM_LH;
  });
};

// Decide whether an order needs the full 4x6 sheet or fits in a 4x3 half-sheet.
// Calculation matches drawLabel layout math at 12pt single-column.
const orderFitsSmall = (doc, order) => {
  doc.setFont('courier', 'bold');
  doc.setFontSize(12);
  // innerWidth at 4x3 (same as 4x6 — width unchanged).
  const innerWidth = (W - FRAME_R - PAD) - (FRAME_L + PAD);
  const lines = buildItemLines(doc, order.items || [], innerWidth);
  // 4x3 label item-area height ≈ 0.76in after carving out room for the
  // hand-write WEIGHT line above the order ID — 3 wrapped lines of 12pt items fit cleanly.
  const SMALL_MAX_LINES = 3;
  return lines.length <= SMALL_MAX_LINES;
};

// Pack labels into 4x6 sheets:
//   • Two small labels share a sheet.
//   • Big labels each get their own full 4x6 sheet.
//   • A lonely small label duplicates to fill the bottom half (so a thermal sheet is never blank).
const packSheets = (entries) => {
  const sheets = [];
  let pendingSmall = null;
  entries.forEach((entry) => {
    if (entry.size === 'big') {
      // Flush pending small first
      if (pendingSmall) {
        sheets.push({ type: 'pair', labels: [pendingSmall, pendingSmall] });
        pendingSmall = null;
      }
      sheets.push({ type: 'big', label: entry });
    } else {
      if (pendingSmall) {
        sheets.push({ type: 'pair', labels: [pendingSmall, entry] });
        pendingSmall = null;
      } else {
        pendingSmall = entry;
      }
    }
  });
  if (pendingSmall) {
    sheets.push({ type: 'pair', labels: [pendingSmall, pendingSmall] });
  }
  return sheets;
};

export const generateLabelsPDF = (orders) => {
  const doc = new jsPDF({ unit: 'in', format: [W, H], orientation: 'portrait' });

  // Decide each order's size.
  const entries = (orders || []).map((order) => ({
    order,
    size: orderFitsSmall(doc, order) ? 'small' : 'big',
  }));

  const sheets = packSheets(entries);

  sheets.forEach((sheet, idx) => {
    if (idx > 0) doc.addPage([W, H], 'portrait');
    if (sheet.type === 'big') {
      drawLabel(doc, sheet.label.order, 0, H);
    } else {
      drawLabel(doc, sheet.labels[0].order, 0, 3);
      drawLabel(doc, sheet.labels[1].order, 3, 3);
    }
  });

  return doc;
};

// Convenience: open in new tab and auto-print.
export const printLabelsPDF = (orders) => {
  const doc = generateLabelsPDF(orders);
  const blobUrl = doc.output('bloburl');
  const win = window.open(blobUrl, '_blank');
  if (win) {
    win.addEventListener('load', () => {
      try { win.focus(); win.print(); } catch (e) { /* ignore */ }
    });
  }
};

export const downloadLabelsPDF = (orders, filename = 'blkmrkt-labels.pdf') => {
  const doc = generateLabelsPDF(orders);
  doc.save(filename);
};
